



















































































import { Component, Ref, Vue, Watch } from 'vue-property-decorator'
import {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  GridOptions,
  ValueFormatterParams
} from 'ag-grid-community'

import { TimesheetActivityColumn as ActivityColumn } from '~/types/globalTypes'

import LayoutGrid from '@/shared/layout/Grid/components/Default.vue'
import MonthYearPicker from '@/shared/Global/components/Inputs/MonthYearPicker.vue'
import DepartmentPicker, {
  Output as Department
} from '@/shared/Department/components/Picker.vue'
import PayoutsTable from '@/shared/HourOverview/components/PayoutsTable.vue'

import {
  TimesheetActivities as ActivitiesResponse,
  TimesheetActivitiesVariables as ActivitiesVariables,
  TimesheetActivities_timesheetActivities_data as Activity
} from '@/shared/TimesheetActivity/apollo/queries/types/TimesheetActivities'
import { TimesheetActivities as ActivitiesQuery } from '@/shared/TimesheetActivity/apollo/queries'
import {
  TimesheetBalanceControl as RowsResponse,
  TimesheetBalanceControlVariables as RowsVariables,
  TimesheetBalanceControl_timesheetBalanceControl as Row
} from '@/shared/HourOverview/apollo/queries/types/TimesheetBalanceControl'
import { TimesheetBalanceControl as RowsQuery } from '@/shared/HourOverview/apollo/queries'

type BalanceField =
  | 'openingMinutes'
  | 'monthMinutes'
  | 'bookingsMinutes'
  | 'closingMinutes'

@Component<BeTimesheetBalanceControlPage>({
  name: 'BeTimesheetBalanceControlPage',
  components: { LayoutGrid, MonthYearPicker, DepartmentPicker, PayoutsTable },
  apollo: {
    activities: {
      query: ActivitiesQuery,
      variables(): ActivitiesVariables {
        return {
          first: this.$constants.paginationUnlimitedItemsCount,
          where: { column: ActivityColumn.USE_BALANCE, value: true }
        }
      },
      update: (res: ActivitiesResponse | null) =>
        res?.timesheetActivities?.data || []
    },
    rows: {
      query: RowsQuery,
      variables(): RowsVariables {
        return {
          month: this.monthDate as string,
          timesheetActivityId: this.activityId as string,
          departmentId: this.department?.id || null
        }
      },
      skip() {
        return !this.activityId || !this.monthDate
      },
      update: (res: RowsResponse | null) => res?.timesheetBalanceControl || [],
      fetchPolicy: 'no-cache'
    }
  }
})
export default class BeTimesheetBalanceControlPage extends Vue {
  month: number | null = null
  year: number | null = null
  activityId: string | null = null
  department: Department | null = null
  hideDepartmentPicker = false

  activities: Activity[] = []
  rows: Row[] = []

  showPayoutsTable = false

  @Ref() readonly payoutTable?: PayoutsTable

  created() {
    const today = this.$today()
    this.month = today.month
    this.year = today.year
  }

  @Watch('activities')
  onActivitiesUpdate(items: Activity[]) {
    if (this.activityId || !items.length) return

    const preset = `${this.$app.options
      .timesheet_overhour_balance_timesheetacitivity || ''}`
    this.activityId = items.find(a => a.id === preset)?.id || items[0].id
  }

  get monthDate(): string | null {
    if (!this.month || !this.year) return null

    return `${this.year}-${`${this.month}`.padStart(2, '0')}-01`
  }

  get gridOptions(): GridOptions {
    return {
      groupDisplayType: 'singleColumn',
      groupDefaultExpanded: 1,
      suppressAggFuncInHeader: true,
      autoGroupColumnDef: {
        headerName: `${this.$tc('department.titles.entryChoice')}`,
        minWidth: 260,
        sortable: true,
        filter: 'agGroupColumnFilter'
      }
    }
  }

  get columns(): ColDef[] {
    return [
      {
        colId: 'department',
        field: 'departmentName',
        headerName: `${this.$tc('department.titles.entryChoice')}`,
        rowGroup: true,
        hide: true,
        valueGetter: ({ data }) =>
          data?.departmentName ||
          `${this.$t('hourOverview.balanceControl.noDepartment')}`
      },
      {
        colId: 'userName',
        field: 'userName',
        headerName: `${this.$t('hourOverview.timesheet.user')}`,
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 220
      },
      this.balanceColumn('openingMinutes', 'opening', true),
      this.balanceColumn('monthMinutes', 'month', false),
      {
        ...this.balanceColumn('bookingsMinutes', 'bookings', false),
        headerTooltip: `${this.$t('hourOverview.balanceControl.bookingsHint')}`
      },
      this.balanceColumn('closingMinutes', 'closing', true)
    ]
  }

  private balanceColumn(
    field: BalanceField,
    label: string,
    clickable: boolean
  ): ColDef {
    return {
      colId: field,
      headerName: `${this.$t(`hourOverview.balanceControl.fields.${label}`)}`,
      type: 'rightAligned',
      sortable: true,
      filter: 'agNumberColumnFilter',
      aggFunc: 'sum',
      valueGetter: ({ data }) =>
        data ? Math.round((data[field] / 60) * 100) / 100 : undefined,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        value || value === 0 ? this.$formatMinutes(Math.round(value * 60)) : '',
      cellClassRules: {
        'error--text': ({ value }: CellClassParams) => Number(value) < 0,
        'balance-control-clickable': ({ node }: CellClassParams) =>
          clickable && !node.group
      },
      onCellClicked: clickable
        ? (event: CellClickedEvent) => this.openPayouts(event, field)
        : undefined
    }
  }

  private openPayouts(event: CellClickedEvent, field: BalanceField) {
    const row = event.data as Row | undefined
    if (!row || event.node.group || !this.monthDate || !this.activityId) return

    const monthStart = this.$normalizeDate(this.monthDate).startOf('month')
    const target =
      field === 'openingMinutes' ? monthStart.minus({ month: 1 }) : monthStart
    const startDate = this.$formatDate(target.startOf('month'), 'payloadDate')
    const endDate = this.$formatDate(target.endOf('month'), 'payloadDate')
    const departmentId = this.department?.id || null
    const activityId = this.activityId

    this.showPayoutsTable = true
    const apply = () => {
      const table = this.payoutTable
      if (!table) return false
      table.startDate = startDate
      table.endDate = endDate
      table.departmentId = departmentId
      table.userIds = [row.userId]
      table.timesheetActivityIds = [activityId]
      table.callback = () => {
        void this.$apollo.queries.rows.refetch()
      }
      table.onOpenTable()
      return true
    }
    this.$nextTick(() => {
      if (!apply()) this.$nextTick(apply)
    })
  }
}
